import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatNumber } from "@/lib/format";
import {
  ImagesIcon, CircleNotchIcon, MagicWandIcon, PaletteIcon, TextTIcon,
  SmileyWinkIcon, CheckCircleIcon, ImageIcon,
} from "@phosphor-icons/react";

const ThumbnailInsights = ({ initial, channelId }) => {
  const [data, setData] = useState(initial || null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/ai/thumbnail-insights${channelId ? `?channel_id=${channelId}` : ""}`);
      setData(res.data);
      toast.success("Thumbnail style decoded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Thumbnail analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const facts = data
    ? [
        { icon: PaletteIcon, label: "Color Strategy", value: data.color_strategy },
        { icon: TextTIcon, label: "Text Usage", value: data.text_usage },
        { icon: SmileyWinkIcon, label: "Faces & Emotion", value: data.face_emotion },
      ].filter((f) => f.value)
    : [];

  return (
    <motion.div
      data-testid="thumbnail-insights"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative bg-[#0C0C0E] border border-[#A3E635]/30 p-5 md:p-6"
      style={{ boxShadow: "0 0 15px rgba(163, 230, 53,0.08), inset 0 1px 0 rgba(255,255,255,0.05)" }}
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full border border-[#A3E635]/50 flex items-center justify-center">
            <ImagesIcon size={20} className="text-[#A3E635]" weight="fill" />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Content Overwatch Vision</div>
            <h2 className="font-rajdhani font-bold text-xl tracking-wide">THUMBNAIL INSIGHTS</h2>
          </div>
        </div>
        <button
          data-testid="analyze-thumbnails-button"
          onClick={run}
          disabled={loading}
          className="flex items-center gap-2 bg-[#A3E635] hover:bg-[#84CC16] text-black disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors duration-200 shrink-0"
        >
          {loading ? <CircleNotchIcon size={14} className="spin-slow" /> : <MagicWandIcon size={14} weight="fill" />}
          {data ? "Re-scan" : "Analyze Thumbnails"}
        </button>
      </div>

      <AnimatePresence mode="wait">
        {!data ? (
          <div className="py-8 text-center">
            <ImageIcon size={30} className="text-[#5a5a63] mx-auto mb-3" />
            <p className="font-mono text-xs text-[#5a5a63] max-w-md mx-auto">
              Content Overwatch looks at your top video thumbnails and tells you the visual style that wins clicks — so you can replicate it.
            </p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
            {/* Analyzed thumbnails strip */}
            {data.analyzed?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="analyzed-thumbnails">
                {data.analyzed.map((t) => (
                  <div key={t.video_id} className="relative shrink-0 w-32">
                    <img src={t.thumbnail} alt={t.title} className="w-32 aspect-video object-cover border border-[#222226]" />
                    <span className="absolute bottom-1 right-1 bg-black/85 text-white font-mono text-[9px] px-1">
                      {formatNumber(t.views)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {data.overall_style && (
              <div className="flex items-start gap-2 border-l-2 border-[#A3E635] pl-3">
                <MagicWandIcon size={16} className="text-[#A3E635] shrink-0 mt-0.5" weight="fill" />
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3E635]">Winning Style: </span>
                  <span className="text-sm text-[#d4d4d8]">{data.overall_style}</span>
                </div>
              </div>
            )}

            {(data.winning_elements || []).length > 0 && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#16C784] mb-2">// What Wins Clicks</div>
                <ul className="space-y-1.5">
                  {data.winning_elements.map((w, i) => (
                    <li key={i} data-testid={`winning-element-${i}`} className="flex gap-2 text-sm text-[#d4d4d8]">
                      <span className="text-[#16C784] font-mono">{String(i + 1).padStart(2, "0")}</span>{w}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {facts.length > 0 && (
              <div className="grid sm:grid-cols-3 gap-3">
                {facts.map((f, i) => (
                  <div key={i} className="border border-[#222226] bg-[#050505] p-3">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#5a5a63] mb-1">
                      <f.icon size={13} className="text-[#A3E635]" /> {f.label}
                    </div>
                    <p className="text-xs text-[#d4d4d8]">{f.value}</p>
                  </div>
                ))}
              </div>
            )}

            {(data.recommendations || []).length > 0 && (
              <div className="border border-[#A3E635]/30 bg-[#050505] p-4">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-2">
                  <CheckCircleIcon size={14} weight="fill" /> Replicate This
                </div>
                <ul className="space-y-1.5">
                  {data.recommendations.map((r, i) => (
                    <li key={i} data-testid={`thumb-rec-${i}`} className="text-sm text-[#d4d4d8] flex gap-2">
                      <span className="text-[#A3E635]">▸</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ThumbnailInsights;
